if(!customElements.get('cart-gwp-selector')) {
  class CartGWPSelector extends HTMLElement {
    constructor() {
      super();
    }

    async connectedCallback(){
      await this.init();
    }

    // helper functions

    isEmptyObject(obj) {
      return Object.keys(obj).length === 0 && obj.constructor === Object;
    }

    isEmptyArray(arr) {
      return Array.isArray(arr) && arr.length === 0;
    }

    deleteSelf(){
      this.remove();
    }

    // main component functions

    async init(){
      const scriptTag = this.querySelector(`script[id="${this.id}-data"]`);
      this.settings = (scriptTag)? JSON.parse(scriptTag.textContent) : {};
      // scrub the original script tag from the DOM, now we are storing it's contents as component data.
      this.removeChild(scriptTag);
      // if we have no products the customer can earn, end the lifecycle.
      this.checkProductsEligibility();
      // DOM elements
      this.cartDrawer = this.closest('cart-drawer');
      this.spinner = this.querySelector('.loading-overlay__spinner');
      this.componentMessage = this.querySelector('.gwp-messaging');
      this.gwpSelector = this.querySelector('.gwp-product-selector');
      this.gwpSelected = this.querySelector('.gwp-selected-product');
      this.gwpSelectedTitle = this.gwpSelected?.querySelector('.gwp-selected-product-title');
      this.isOpen = false;
      this.gwpOptions = this.querySelector('.gwp-product-options');
      /*
        Initialize the component.
        The component will most likely "survive" only through the initGWPForm function.
        Any instance of adding / removing a product from cart should trigger this.cartDrawer.refresh().
        this.cartDrawer.refresh() completely replaces the DOM of this.cartDrawer, thus ending the lifecycle of the current component.
        The new component will init() and based upon changes to the cart, potentially produce a new render.
      */
      try {
        this.initEventListeners();
        await this.initGWPForm();
      } catch (error) {
        console.error('Error initializing component:', error);
      }
    }

    async fetchCart(){
      // fetch the Shopify cart via a special view that only returns the cart subtotal, and an existing GWP if possible.
      try{
        return fetch(`/cart.js`, { method: 'GET' })
        .then(response => response.json())
        .then((data) => {
          return data;
        })
        .catch((error) => {
          console.error(error);
        });
      } catch (error) {
        console.error(error);
        throw error;  // Propagate the error to the calling function
      }
    }

    checkProductsEligibility() {
      // lifecycle ending function. If we cannot source any products the customer has the potential to earn, we don't need to occupy space.
      if (this.isEmptyArray(this.settings.products)) {
        this.deleteSelf();
        return false;
      }
      return true;
    }

    sortProductsList(){
      // sort all products a customer could earn from lowest required cart spend to the highest.
      if(this.isEmptyArray(this.settings.products)) return [];
      this.settings.products.sort((a, b) => a.threshold - b.threshold);
    }

    getEligibleProducts() {
      if(this.isEmptyArray(this.settings.products)) return [];
      // loop through this.settings.products and return an array of every object where the threshold is greater than this.cartSubtotal
      return this.settings.products.filter(product => product.threshold <= this.cartSubtotal);
    }

    isCartGWPEligible() {
      if(!this.cartGWP || this.isEmptyArray(this.eligibleGWPs)) return false;
      return this.eligibleGWPs.some(gwp => gwp.variant.id == this.cartGWP.id);
    }

    getEligibleGWPById(id) {
      const foundGWP = this.eligibleGWPs.find(gwp => gwp.variant.id == id);
      return foundGWP || null;
    }

    initEventListeners(){
      this.gwpSelected?.addEventListener("click", (event)=>{
        event.stopPropagation();
        this.isOpen = !this.isOpen;
        if(this.isOpen){
          this.gwpSelected.setAttribute('open', '');
        } else {
          this.gwpSelected.removeAttribute('open');
        }
      });
    }

    async handleGWPOptionSelect(option){
      if(!option) return;
      const eligibleGWP = this.getEligibleGWPById(option.dataset.variant);
      if(!eligibleGWP) return;
      await this.addGWPToCart(eligibleGWP);
    }

    setActiveGWP(){
      // no cartGWP? how did we get here, but cancel it.
      if(!this.cartGWP) return;
      // grab the eligibleGWP entry by the cartGWP id
      const eligibleGWP = this.getEligibleGWPById(this.cartGWP.id);
      // unable to find the cartGWP in the list? How did we get here, cancel it.
      if(!eligibleGWP) return;
      // if we can find the selected title element, update the content.
      if(this.gwpSelectedTitle) this.gwpSelectedTitle.innerHTML = `${eligibleGWP.product.title}`;
      // if we can find the option in our dropdown, select it.
      const eligibleOption = this.gwpOptions.querySelector(`.gwp-product-option[data-variant="${eligibleGWP.variant.id}"]`);
      if(eligibleOption) eligibleOption.classList.add('selected');
    }

    async initGWPForm(){
      /*
        This is the core function here. This function will draw out the render the customer will see / interact with.
        Various code that adds / updates / removes products from the cart will call "cartDrawer.refresh()"
        "cartDrawer.refresh()" completely replaces the cartDrawer DOM, thus ending the lifecycle of this element.
        The newly injected instance of this component will init, and potentially render a new result.
      */
      /*
        Start by sorting the potential GWPs from lowest threshold to the highest.
        This will sort this.settings.products in order from lowest spend needed to highest spend needed.
      */
      this.sortProductsList();
      /*
        Fetch the subtotal from the live cart via API, if there is a GWP in the cart, grab that too.
        This will update this.cartGWP and this.cartSubtotal
      */
      const cartData = await this.fetchCart();
      this.cartSubtotal = cartData.items_subtotal_price;
      this.cartGWP = this.getCartGWP(cartData.items);
      /*
        The minimum spend the customer needs to reach to be eligible for their first GWP.
      */
      this.minimumGWPSubtotal = 0;
      if (this.settings.products && this.settings.products.length > 0) {
        this.minimumGWPSubtotal = this.settings.products[0].threshold;
      }
      /*
        Filter down the list of products in this.settings.products to return only the GWP entries the current cart subtotal could redeem, if any.
      */
      this.eligibleGWPs = this.getEligibleProducts();
      /*
        Component messages are based upon templates set up in the cart-drawer section settings.
      */
      if(this.isEmptyArray(this.eligibleGWPs)){
        this.handleNoEligibleGWPs();
        // end the function here
        return;
      };
      /*
        If we are here, this means we have eligible GWPs the customer can purchase.
        check if we have eligible GWPs.
        if we do, check if we have exactly 1 eligible GWP.
          check if we have a GWP already in the cart.
            check if we are auto-adding the first available GWP to the cart.
              if so, add that GWP to the cart.
              otherwise, show the GWP selector & messaging.
        if we have more than 1 eligible GWPs we want to render our special drop down.
          if we have no GWP in the cart, render the messaging for "GWPs available, none selected"
          if we have a GWP in the cart, render the messaging for "GWPs available, already selected"
      */
      /*
        Of all of the eligible GWPs, we want to know what the highest threshold we hit is.
        To find the highest threshold we have hit, we check the LAST entry in the eligibleGWP array since it is in threshold order.
      */
      if(this.eligibleGWPs.length == 1){
        this.handleSingleEligibleGWP();
      } else {
        // At this stage, the customer has spent enough money they are eligible for a GWP, and there are 1+ to choose from.
        this.handleMultipleEligibleGWPs();
      }
    }

    handleNoEligibleGWPs(){
      /*
        If there are no eligible GWPs, but we clearly have some the customer will be eligible for if they spent more, display that messaging.
        A Shopify Script in place should remove any _GWP property item when the cart subtotal is below that line item's _GWP_threshold property.
        Because any quantity events will triger a complete cart drawer refresh, we do not need to handle the removal or refresh here.
      */
      const spendDifference = Shopify.formatMoney((this.minimumGWPSubtotal - this.cartSubtotal), '${{amount}}');
      this.setMessage('noEligibleGWPs', { threshold: spendDifference });
    }

    handleSingleEligibleGWP(){
      if(!(this.cartGWP)){
        // if there is no cartGWP found in the cart from our fetch, the customer is eligible to add this GWP without needing to swap.
        if(this.settings.gwp_auto_atc_first_gwp){
          // this has been disabled for now. If a customer is at the minimum threshold, and removes the GWP it just ends up right back in the cart.
          /*
            The addGWPToCart() to function will call cartDrawer.refresh() which will inject a new instance of this component.
            At this point, the lifecycle of this component is over.
          */
          this.addGWPToCart(this.eligibleGWPs[0]);
          return;
        } else {
          const maximumEligibleGWPSubtotal = this.eligibleGWPs[this.eligibleGWPs.length - 1].threshold;
          const spendMet = Shopify.formatMoney((maximumEligibleGWPSubtotal), '${{amount}}');
          this.setMessage('unselectedEligibleGWP', { threshold: spendMet });
        }
      } else {
        /*
          At this branch, we can infer there is a GWP in the cart.
          Theoretically, the GWP in the cart may not be the GWP the customer is eligible for. The customer -could- have an old GWP in the cart.
          If the variant ID of the cart GWP does NOT match the only eligible GWP, remove that GWP from the cart.
        */
        if(!this.isCartGWPEligible()){
          /*
            The removeGWPFromCart() to function will call cartDrawer.refresh() which will inject a new instance of this component.
            At this point, the lifecycle of this component is over.
          */
          this.removeGWPFromCart(this.cartGWP);
          return;
        } else {
          /*
            We can assume the GWP in the cart is the same variant as the eligible GWP.
            Display single eligible GWP redeemed messaging.
          */
          const spendMet = Shopify.formatMoney((this.minimumGWPSubtotal), '${{amount}}');
          this.setMessage('singleEligibleGWP', { threshold: spendMet });
        }
      }
      if(this.gwpSelector){
        this.populateGWPDropdown();
        this.setActiveGWP();
        if(this.settings.gwp_auto_atc_first_gwp){
          this.gwpSelector.classList.add('hidden');
        } else {
          this.gwpSelector.classList.remove('hidden');
        }

      }
    }

    handleMultipleEligibleGWPs(){
      if(this.gwpSelector){
        // hide the spinner
        this.spinner.classList.add('hidden');
        // if we have a GWP in the cart
        if(this.cartGWP){
          /*
            If the cartGWP is NOT one the eligibleGWPs, assume it is an outdated GWP and we should remove it.
            If we have to remove the cartGWP, the remove function will replace this instance of the component, and end the lifecycle here.
          */
          // Check if the GWP currently in the cart is one of the eligibleGWP variants.
          if(!this.isCartGWPEligible()){
            /*
              If the cartGWP is NOT one the eligibleGWPs, assume it is an outdated GWP and we should remove it.
              If we have to remove the cartGWP, the remove function will replace this instance of the component, and end the lifecycle here.
            */
            this.removeGWPFromCart(this.cartGWP);
          } else {
            // If the cartGWP is one of the eligibleGWPs, show the "multiple eligibleGWPs, and you currently selected one of them"
            const maximumEligibleGWPSubtotal = this.eligibleGWPs[this.eligibleGWPs.length - 1].threshold;
            const spendMet = Shopify.formatMoney((maximumEligibleGWPSubtotal), '${{amount}}');
            this.setMessage('selectedMultipleEligibleGWP', { threshold: spendMet });
          }
        } else {
          /*
            If there is no cartGWP, the customer has eligibleGWPs, but they have not selected one.
            set the message.
          */
          const maximumEligibleGWPSubtotal = this.eligibleGWPs[this.eligibleGWPs.length - 1].threshold;
          const spendMet = Shopify.formatMoney((maximumEligibleGWPSubtotal), '${{amount}}');
          this.setMessage('unselectedEligibleGWP', { threshold: spendMet });
        }
        // if we make it here, show our GWP selector element.
        this.populateGWPDropdown();
        this.setActiveGWP();
        this.gwpSelector.classList.remove('hidden');
      }
    }

    setMessage(type, values = {}) {
      const template = this.getMessageTemplate(type);
      const message = template.replace(/{threshold}/g, values.threshold || '');
      if(this.componentMessage){
        this.componentMessage.innerHTML = message;
        // hide the spinner
        this.spinner.classList.add('hidden');
        // display the message.
        this.componentMessage.classList.remove('hidden');
      }
    }

    getMessageTemplate(type) {
      const templates = {
        noEligibleGWPs: this.settings.strings.gwp_initial_spend_template,
        singleEligibleGWP: this.settings.strings.gwp_single_set_template,
        unselectedEligibleGWP: this.settings.strings.gwp_initial_met_template,
        selectedMultipleEligibleGWP: this.settings.strings.gwp_multiple_set_template
      };
      return templates[type] || 'Unknown message type';
    }

    populateGWPDropdown() {
      const optionTemplate = this.querySelector(`#${this.getAttribute('id')}-gwp-option-template`);
      if(!optionTemplate) return;

      const gwpOptionsList = this.querySelector('.gwp-product-options');
      if(!gwpOptionsList) return;

      gwpOptionsList.innerHTML = "";

      this.eligibleGWPs.forEach((product) => {
        const option = optionTemplate.content.firstElementChild.cloneNode(true);
        option.dataset.variant = product.variant.id;
        const img = option.querySelector('.gwp-product-media');
        img.src = product.variant.featured_media;
        img.alt = product.product.title;

        const title = option.querySelector('.gwp-product-title');
        title.innerHTML = product.product.title;

        gwpOptionsList.appendChild(option);

        option.addEventListener("click", (event)=>{
          this.handleGWPOptionSelect(option);
        });
      });
    }

    refreshCartDrawer() {
      try {
        // if this.cartDrawer.refresh() resolves, this will essentially end the lifecycle of this component instance.
        // this.cartDrawer.refresh() will replace the dom of the existing <cart-drawer> with one from a fetch.
        if(this.cartDrawer) this.cartDrawer.refresh();
      } catch (error) {
        console.error(error);
      }
    }

    async removeProductFromCart(body, refresh = true) {
      try {
        const response = await fetch(`${window.theme.routes.cart_update_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: body })
        });
        const data = await response.json();
        if (data.status) {
          console.error(data.description);
          return;
        }
        // if refresh is true, refresh the cartDrawer and "end this session"
        if (refresh) this.refreshCartDrawer();
      } catch (error) {
        console.error(error);
      }
    }

    async addProductToCart(body) {
      try {
        const response = await fetch(`${window.theme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(body)
        });
        const data = await response.json();
        if (data.status) {
          console.error(data.description);
          return;
        }
        this.refreshCartDrawer();
      } catch (error) {
        console.error(error);
      }
    }

    getCartGWP(items){
      if(this.isEmptyArray(items)) return null;
      const foundGWP = items.find(line_item => line_item.properties?._GWP);
      return foundGWP || null;
    }

    getComprehensiveGWPVariant = (gwp, id) => {
      if (!gwp?.data) return {};
      return gwp.data?.variants?.find(variant => variant.id === id) ?? {};
    }

    async addGWPToCart(gwp) {
      if (!gwp) return;

      try {
        // if we have a cartGWP, remove it.
        const cartData = await this.fetchCart();
        this.cartSubtotal = cartData.items_subtotal_price;
        this.cartGWP = this.getCartGWP(cartData.items);
        if (this.cartGWP) {
          const updates = {};
          updates[`${this.cartGWP.key}`] = 0;
          await this.removeProductFromCart(updates, false);
        }

        const gwp_varaint = this.getComprehensiveGWPVariant(gwp, gwp.variant.id);
        const gwp_color_id = gwp_varaint.color ?? "";
        const atcItem = {
          id: gwp.variant.id,
          quantity: 1,
          properties: {
            "_GWP": true,
            "_GWP_threshold": gwp.threshold,
            "_GWP_threshold_formatted": Shopify.formatMoney(gwp.threshold, '${{amount}}'),
            "_color_id": gwp_color_id
          }
        };

        if(gwp.variant.cart_message){
          atcItem.properties["_GWP_cart_message"] = gwp.variant.cart_message;
        }

        const customerId = document.querySelector('.js-customer-id')?.value || null;
        if(customerId){
          atcItem.properties["_intuitive_cid"] = `${customerId}`;
        }

        // at this point, add the desired GWP to the cart.
        const addBody = {
          items: [atcItem]
        };

        await this.addProductToCart(addBody);
      } catch (error) {
        console.error(error);
      }
    }

    async removeGWPFromCart(gwp){
      if(!gwp) return;
      try {
        const updates = {};
        updates[`${this.cartGWP.key}`] = 0;

        await this.removeProductFromCart(updates);
      } catch (error) {
        console.error(error);
      }
    }

  }
  customElements.define('cart-gwp-selector', CartGWPSelector);
}
